import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function LeadInspectionsPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const whatWeTest = [
    {
      icon: 'ri-paint-brush-line',
      title: 'Painted Surfaces',
      description: 'Walls, trim, doors, windows, and exterior surfaces in homes built before 1978.',
    },
    {
      icon: 'ri-home-4-line',
      title: 'Common Areas',
      description: 'Hallways, stairwells, and shared spaces in multi-family housing and daycares.',
    },
    {
      icon: 'ri-drop-line',
      title: 'Dust & Soil',
      description: 'Floor dust, window sills, and exterior soil where children play.',
    },
    {
      icon: 'ri-file-text-line',
      title: 'Compliance Documentation',
      description: 'Reports that meet EPA, HUD, and state requirements for disclosure and clearance.',
    },
  ];

  const whoNeeds = [
    {
      title: 'Homeowners & Buyers',
      description: 'Required for homes built before 1978 during sale, rental, or renovation.',
    },
    {
      title: 'Landlords & Property Managers',
      description: 'Mandatory for rental properties and multi-family housing in many states.',
    },
    {
      title: 'Daycares & Schools',
      description: 'Protect children with regular lead testing and risk assessments.',
    },
    {
      title: 'Commercial Property Owners',
      description: 'Due diligence for acquisitions, renovations, and tenant safety.',
    },
    {
      title: 'Military & Public Housing',
      description: 'Large-scale portfolio testing with standardized protocols and data systems.',
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Schedule & Plan',
      description: 'We review your property type, age, and testing goals, then schedule a convenient time.',
    },
    {
      step: '02',
      title: 'On-Site Inspection',
      description: 'EPA-certified inspectors use XRF analyzers and collect paint chip and dust samples.',
    },
    {
      step: '03',
      title: 'Lab Analysis',
      description: 'Samples are analyzed by accredited laboratories following EPA protocols.',
    },
    {
      step: '04',
      title: 'Clear Report & Guidance',
      description: 'You receive a detailed report with findings, risk levels, and next steps—no jargon.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Lead Inspections & Risk Assessments
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Comprehensive lead-based paint testing and risk evaluation for residential and commercial properties. We help property owners, managers, and contractors identify lead hazards and develop practical solutions that meet federal and state requirements.
            </p>
            <Link
              to="/contact#contact-form"
              className="inline-block bg-[#f59e0b] text-white px-10 py-4 rounded-lg text-base font-semibold hover:bg-[#d97706] transition-colors whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
            >
              Request an Inspection
            </Link>
          </div>
        </div>
      </section>

      {/* What We Test */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What We Test For</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our lead inspections cover all potential sources of lead exposure in your property.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whatWeTest.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                  <i className={`${item.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Needs This */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Who Needs Lead Inspections?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Lead testing is required or recommended for a wide range of properties and situations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {whoNeeds.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-shadow duration-300"
              >
                <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Our Inspection Process</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From scheduling to final report, we make lead testing straightforward and stress-free.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-8 border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300 h-full">
                  <div className="text-5xl font-bold text-[#f59e0b]/20 mb-4">{item.step}</div>
                  <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose LCA */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Why Choose LCA for Lead Testing?</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-shield-check-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">EPA-Certified Inspectors</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                All inspectors are EPA-certified and state-licensed with years of field experience.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-file-list-3-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Clear, Defensible Reports</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Reports designed to meet EPA, HUD, and state requirements—no confusing jargon.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center border border-gray-200 hover:border-[#f59e0b] hover:shadow-lg transition-all duration-300">
              <div className="w-16 h-16 bg-[#f59e0b] text-white rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className="ri-database-2-line text-3xl"></i>
              </div>
              <h3 className="text-lg font-bold text-black mb-3">Portfolio-Scale Systems</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Web-based data systems for large housing portfolios and multi-site programs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#f59e0b] to-[#d97706] rounded-3xl p-12 text-center shadow-2xl">
            <h2 className="text-4xl font-bold text-white mb-6">
              Need a Lead Inspection or Risk Assessment?
            </h2>
            <p className="text-xl text-white/95 mb-8 leading-relaxed">
              Whether you're buying, selling, renovating, or managing properties, we'll help you understand your lead risks and stay compliant.
            </p>
            <Link
              to="/contact#contact-form"
              className="inline-block bg-white text-[#f59e0b] px-10 py-4 rounded-lg text-base font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
            >
              Schedule an Inspection
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
